/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component, Children } from 'react';
import cx from 'classnames';
import autobind from 'class-autobind';

import Checkbox from './CheckboxOld';
import isObject from '../../support/shared/isObject';

type Props = {
  children?: React.ReactNode;
  className?: string;
  onSelectionChange?: (...args: Array<any>) => any;
  showSelectAll?: boolean;
};

type State = {
  childList: Array<React.ReactNode>;
  areAllChecked: boolean;
};

export default class CheckboxList extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    autobind(this);
    let { childList, areAllChecked } = this._parseChildren(this.props.children);
    this.state = {
      childList,
      areAllChecked,
    };
  }

  componentDidUpdate(oldProps: Props) {
    let newProps = this.props;
    if (newProps.children !== oldProps.children) {
      let { childList, areAllChecked } = this._parseChildren(newProps.children);
      this.setState({
        childList,
        areAllChecked,
      });
    }
  }

  render() {
    let {
      className,
      onSelectionChange,
      showSelectAll,
      ...otherProps
    } = this.props;
    return (
      <div {...otherProps} className={cx(className, 'checkbox-list')}>
        <div className="checkbox-list-inner">
          {this._renderSelectAll()}
          {this._renderChildren(this.state.childList)}
        </div>
      </div>
    );
  }

  _renderSelectAll() {
    if (!this.props.showSelectAll) {
      return;
    }
    let { areAllChecked } = this.state;
    return (
      <Label
        noDoubleClickSelect={true}
        className="checkbox-list-item checkbox-list-item-header-row"
      >
        <span className="checkbox-list-item-check">
          <Checkbox
            name="[all]"
            onChange={this._onSelectAllChange}
            checked={areAllChecked}
          />
        </span>
        <CheckboxItem>(select all)</CheckboxItem>
      </Label>
    );
  }

  _renderChildren(children: Array<React.ReactNode>): Array<React.ReactNode> {
    return children.map((child: unknown) => {
      if (isObject(child)) {
        let key = typeof child.key === 'string' ? child.key : '';
        let props = isObject(child.props) ? child.props : {};
        return (
          <Label
            key={key}
            noDoubleClickSelect={true}
            className="checkbox-list-item"
          >
            <span className="checkbox-list-item-check">
              <Checkbox
                name={key}
                onChange={this._onChange.bind(null, key)}
                checked={!!props.isChecked}
              />
            </span>
            {child}
          </Label>
        );
      }
      return null;
    });
  }

  _parseChildren(children?: React.ReactNode) {
    let childList: Array<React.ReactNode> = [];
    let areAllChecked = true;
    Children.forEach(children, (child: unknown) => {
      if (isObject(child)) {
        if (child.type !== CheckboxItem) {
          throw new Error(
            'Each child of `CheckboxList` must be of type `CheckboxItem`.',
          );
        }
        if (child.key == null) {
          throw new Error('Each child of `CheckboxList` must have a key.');
        }
        let props = isObject(child.props) ? child.props : null;
        if (props && props.isChecked !== true) {
          areAllChecked = false;
        }
      }
      childList.push(child as any);
    });
    return { childList, areAllChecked };
  }

  _onSelectAllChange() {
    let { areAllChecked } = this.state;
    let selectedKeys = new Set();
    if (!areAllChecked) {
      for (let child of this.state.childList as Array<unknown>) {
        let key =
          isObject(child) && typeof child.key === 'string' ? child.key : '';
        selectedKeys.add(key);
      }
    }
    if (this.props.onSelectionChange) {
      this.props.onSelectionChange(selectedKeys);
    }
  }

  _onChange(changedKey: string, isChecked: boolean) {
    if (!this.props.onSelectionChange) {
      return;
    }
    let selectedKeys = new Set();
    for (let child of this.state.childList as Array<unknown>) {
      let key =
        isObject(child) && typeof child.key === 'string' ? child.key : '';
      let props = isObject(child) && isObject(child.props) ? child.props : {};
      let shouldAddKey =
        key === changedKey ? isChecked : props.isChecked === true;
      if (shouldAddKey) {
        selectedKeys.add(key);
      }
    }
    this.props.onSelectionChange(selectedKeys);
  }
}

type CheckboxItemProps = {
  className?: string;
  isChecked?: boolean | null | undefined;
};

export class CheckboxItem extends Component<CheckboxItemProps> {
  render() {
    let { className, isChecked, ...otherProps } = this.props;
    className = cx(className, 'checkbox-list-item-value');
    return <span className={className} {...otherProps} />;
  }
}

type LabelProps = {
  children?: React.ReactNode;
  className?: string;
  noDoubleClickSelect?: boolean;
  onMouseDown?: (...args: Array<any>) => any;
  onMouseUp?: (...args: Array<any>) => any;
};

type LabelState = {
  disableSelection: boolean;
};

class Label extends Component<LabelProps, LabelState> {
  _timeout: TimeoutID;
  _lastMouseUp: number | undefined;

  constructor(props: LabelProps) {
    super(props);
    autobind(this);
    this.state = {
      disableSelection: false,
    };
  }

  componentWillUnmount() {
    clearTimeout(this._timeout);
  }

  render() {
    let { className, noDoubleClickSelect, ...props } = this.props;
    if (noDoubleClickSelect && this.state.disableSelection) {
      className = cx(className, 'no-select');
    }
    return (
      <label
        {...props}
        onMouseDown={this._onMouseDown}
        onMouseUp={this._onMouseUp}
        className={className}
      >
        {this.props.children}
      </label>
    );
  }

  _onMouseDown(...args: any) {
    if (this._lastMouseUp != null) {
      let timeSinceLastClick = Date.now() - this._lastMouseUp;
      if (timeSinceLastClick < 500) {
        this.setState({ disableSelection: true });
        clearTimeout(this._timeout);
        this._timeout = setTimeout(() => {
          this.setState({ disableSelection: false });
        }, 500);
      }
    }
    if (this.props.onMouseDown) {
      this.props.onMouseDown(...args);
    }
  }

  _onMouseUp(...args: any) {
    this._lastMouseUp = Date.now();
    if (this.props.onMouseUp) {
      this.props.onMouseUp(...args);
    }
  }
}
